import React, { useEffect, useState } from 'react';
import { Button } from '@asigna/kit';
import cx from 'classnames';
import { MAX_NOTES_LENGTH, NotesInput } from 'components';
import useAddress from 'hooks/use-address';
// import { useCountTxFee } from 'hooks/use-count-tx-fee';
import useNetwork from 'hooks/use-network';
import useSafe from 'hooks/use-safe';
import { useSafeCalls } from 'hooks/use-safe-call';
import { isEmpty, isUndefined } from 'lodash';
import { generateUnsignedStxTransfer } from 'utils/transaction-create';

import { SetTxFee } from '../components/SetTxFee';

import DropDownList from './DropDownList';
import { NonceInput } from './NonceInput';
import { TransactionCommonDialog } from './TransactionCommonDialog';
import { checkForRecipientError } from './utils';

interface TransactionCommonProps {
  open: boolean;
  transferredAsset: string;
  safeAddress?: string;
  selectedTokenIndex?: number;
  onClose: () => void;
  onContinue: (successful: boolean, message: string) => void;
}

export const SendStxsDialog = (props: TransactionCommonProps) => {
  const { open, transferredAsset, onClose, onContinue, safeAddress } = props;

  const walletAccount = useAddress();
  const { safe } = useSafe();
  const [, stacksNetwork, ,] = useNetwork();
  // const { countTxGas } = useCountTxFee();

  const [currentStep, setCurrentStep] = useState<1 | 2>(1);
  const [recepientAddress, setRecepientAddress] = useState('');
  const [recepientAddressErrorMessage, setRecepientAddressErrorMessage] = useState('');
  const [nonce, setNonce] = useState<number>();
  const [hasNonceError, setHasNonceError] = useState(false);
  const [generatedTxHex, setGeneratedTxHex] = useState('');
  const [hasAnError, setHasAnError] = useState(false);
  const [notesValue, setNotesValue] = useState('');

  const { nativeSendSTX20 } = useSafeCalls();

  const handleSend = async (fee: number) => {
    if (isUndefined(nonce)) return onContinue(false, 'Transaction is not created');
    const response = await nativeSendSTX20(nonce, recepientAddress, transferredAsset, notesValue);

    onContinue(
      response.isSuccessful,
      response.isSuccessful
        ? `Transaction is created and added to the Queue tab`
        : 'Transaction is not created'
    );
    onClose();
  };

  const handleNextStepClick = () => {
    if (isUndefined(nonce) || !walletAccount) return;

    const generateTx = () =>
      generateUnsignedStxTransfer(
        nonce,
        walletAccount,
        1,
        stacksNetwork,
        safe.owners.map((owner) => owner.publicKey),
        safe.threshold,
        safe.address,
        0,
        transferredAsset
      );

    generateTx().then((txHash) => {
      setGeneratedTxHex(txHash);
      setCurrentStep(2);
    });
  };

  useEffect(
    function validateForErrors() {
      setRecepientAddressErrorMessage('');
      setHasAnError(
        checkForRecipientError(recepientAddress, safeAddress, setRecepientAddressErrorMessage)
      );
    },
    [recepientAddress]
  );

  const continueButton = () => {
    if (currentStep === 2) return <></>;

    return (
      <Button
        fullWidth
        colorType="primary"
        onClick={() => handleNextStepClick()}
        disabled={
          hasAnError || isEmpty(nonce) || hasNonceError || notesValue.length > MAX_NOTES_LENGTH
        }
        message="Next"
      />
    );
  };

  return (
    <TransactionCommonDialog
      open={open}
      title={currentStep === 1 ? `Send ${transferredAsset}` : 'Choose Fee'}
      button={continueButton()}
      onClose={onClose}
    >
      <div className={cx('w-full flex-col my-32 gap-24', currentStep === 1 ? 'flex' : 'hidden')}>
        {safeAddress && (
          <NonceInput
            value={nonce}
            onChange={(newNonce) => setNonce(newNonce)}
            onError={(hasError) => setHasNonceError(hasError)}
          />
        )}
        <>
          <DropDownList
            onChange={(address) => setRecepientAddress(address)}
            placeholder="Recipient (address or BNS name)"
            value={recepientAddress}
          />
          {recepientAddressErrorMessage.length > 0 && (
            <div style={{ marginTop: '8px', color: '#e44e45' }}>{recepientAddressErrorMessage}</div>
          )}
        </>
        <NotesInput value={notesValue} onChange={setNotesValue} className="mt-0" />
      </div>
      <div className={currentStep === 2 ? 'flex' : 'hidden'}>
        <SetTxFee
          onBackClick={() => setCurrentStep(1)}
          onSend={handleSend}
          transactionHex={generatedTxHex}
        />
      </div>
    </TransactionCommonDialog>
  );
};
